import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import API_BASE_URL from '../config/api';
import '../styles/Profile.css';

const Profile = ({ user, onLogout }) => {
  const [profile, setProfile] = useState(user);
  const [stats, setStats] = useState({
    tasksCreated: 0,
    quizScore: 0,
    notesRevealed: 0,
    totalQuizzes: 0,
    totalNotes: 0,
    completedTasks: 0,
    partnerName: '',
    daysConnected: 0
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: user?.name || '',
    email: user?.email || ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const [profileResponse, statsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/auth/profile`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }),
        fetch(`${API_BASE_URL}/api/auth/stats`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
      ]);

      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setProfile(profileData);
      }

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditForm({
      name: profile.name,
      email: profile.email
    });
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setProfile(updatedUser);
        setIsEditing(false);
        alert('Profile updated successfully! 💕');
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile. Please try again.');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      name: profile.name,
      email: profile.email
    });
  };

  const handleChange = (e) => {
    setEditForm({
      ...editForm,
      [e.target.name]: e.target.value
    });
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout? 💕')) {
      onLogout();
    }
  };

  const connectPartner = async () => {
    const partnerEmail = prompt('Enter your partner\'s email address to send connection request:');
    if (partnerEmail && partnerEmail.trim()) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/connect-partner`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ partnerEmail: partnerEmail.trim() })
        });

        const data = await response.json();
        if (response.ok) {
          alert('Connection request sent! 💕');
          fetchProfileData(); // Refresh data
        } else {
          alert(data.message || 'Failed to connect with partner');
        }
      } catch (error) {
        console.error('Error connecting partner:', error);
        alert('Error connecting partner. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="profile">
        <div className="loading">Loading profile... 💕</div>
        <Navigation />
      </div>
    );
  }

  return (
    <div className="profile">
      <div className="container">
        <div className="profile-header">
          <div className="avatar-section">
            <div className="avatar">
              {profile?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="online-indicator"></div>
          </div>

          {isEditing ? (
            <div className="edit-form">
              <input
                type="text"
                name="name"
                value={editForm.name}
                onChange={handleChange}
                className="edit-input"
                placeholder="Name"
              />
              <input
                type="email"
                name="email"
                value={editForm.email}
                onChange={handleChange}
                className="edit-input"
                placeholder="Email"
              />
              <div className="edit-buttons">
                <button onClick={handleSave} className="btn btn-primary btn-sm">
                  💾 Save
                </button>
                <button onClick={handleCancel} className="btn btn-secondary btn-sm">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-info">
              <h1>{profile?.name}</h1>
              <p className="couple-info">
                Part of couple: <span className="couple-id">{profile?.coupleId}</span>
              </p>
              {stats.partnerName && (
                <p className="partner-info">💕 Connected with {stats.partnerName}</p>
              )}
              <button onClick={handleEdit} className="btn btn-outline">
                ✏️ Edit Profile
              </button>
            </div>
          )}
        </div>

        <div className="stats-section">
          <h2>Your Love Stats</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-icon">📝</div>
              <div className="stat-content">
                <h3>Tasks Created</h3>
                <span className="stat-number">{stats.tasksCreated || 0}</span>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-icon">🏆</div>
              <div className="stat-content">
                <h3>Best Quiz Score</h3>
                <span className="stat-number">{stats.quizScore || 0}</span>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-icon">💌</div>
              <div className="stat-content">
                <h3>Love Notes Sent</h3>
                <span className="stat-number">{stats.totalNotes || 0}</span>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <h3>Quizzes Created</h3>
                <span className="stat-number">{stats.totalQuizzes || 0}</span>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>Tasks Completed</h3>
                <span className="stat-number">{stats.completedTasks || 0}</span>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-icon">💕</div>
              <div className="stat-content">
                <h3>Days Together</h3>
                <span className="stat-number">{stats.daysConnected || 0}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="couple-section">
          <h2>Couple Information</h2>
          <div className="couple-card">
            <div className="couple-details">
              <p><strong>Couple Code:</strong> {profile?.coupleId}</p>
              <p><strong>Member Since:</strong> {new Date(profile?.createdAt).toLocaleDateString()}</p>
              {stats.partnerName ? (
                <p><strong>Partner:</strong> {stats.partnerName} 💕</p>
              ) : (
                <p><strong>Status:</strong> Looking for partner 💔</p>
              )}
            </div>
            {!stats.partnerName && (
              <button onClick={connectPartner} className="btn btn-primary">
                🔗 Connect Partner
              </button>
            )}
          </div>
        </div>

        <div className="settings-section">
          <h2>Settings</h2>
          <div className="settings-list">
            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-icon">🔔</div>
                <div>
                  <h4>Notifications</h4>
                  <p>Get notified about new love notes</p>
                </div>
              </div>
              <label className="toggle">
                <input type="checkbox" defaultChecked />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-icon">🌙</div>
                <div>
                  <h4>Dark Mode</h4>
                  <p>Switch to dark theme</p>
                </div>
              </div>
              <label className="toggle">
                <input type="checkbox" />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting-item clickable" onClick={() => alert('Share your couple code: ' + profile?.coupleId)}>
              <div className="setting-info">
                <div className="setting-icon">📤</div>
                <div>
                  <h4>Share Couple Code</h4>
                  <p>Invite your partner to join</p>
                </div>
              </div>
              <div className="arrow">→</div>
            </div>

            <div className="setting-item clickable" onClick={() => fetchProfileData()}>
              <div className="setting-info">
                <div className="setting-icon">🔄</div>
                <div>
                  <h4>Refresh Data</h4>
                  <p>Update your statistics</p>
                </div>
              </div>
              <div className="arrow">→</div>
            </div>
          </div>
        </div>

        <div className="danger-zone">
          <button onClick={handleLogout} className="btn btn-danger">
            👋 Logout
          </button>
        </div>
      </div>

      <Navigation />
    </div>
  );
};

export default Profile;
